import { useEffect, useMemo } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import { Dataset } from 'reporting-data/v2/dataset/datasetRecords';
import { Map as ImmutableMap, fromJS } from 'immutable';
import { buildDataset } from 'reporting-data/v2/reportingApi/buildDataset';
import { mergeAsyncState } from '../shared/hooks/asyncState';
import useMeta from './internal/useMeta';
import useQueryRaaS, { useQueryRaasWithAsyncReportResolve } from './internal/useQueryRaaS';
import useUpdatingRef from '../shared/hooks/useUpdatingRef';
// @ts-expect-error untyped dependency
import useVisualize from './internal/useVisualize';
import { applyRaaSDatasetFixes } from './internal/raasSnowflakeDatasetFixes';
import { SUM } from 'reporting-data/constants/metricTypes';
import { useSnowflakeJoinMetadata } from 'reporting-snowflake/relational/metadata/joins-with-meta';
import { DATASET_INSIGHT_DATASET_PREFIX } from 'reporting-data/tsTypes/datasetInsightTypes';
import { isUngatedForGate } from 'reporting-data/utils/configurationOverwrite';
import { gate } from 'hub-http/gates';
const relationalAsyncReportResolveGate = gate('ReportingViz:useRelationalAsyncReportResolve');
const getDatasetBuildOptionsFromReportDef = reportDef => {
  return {
    isSearch: reportDef.get('columns', ImmutableMap()).every(column => column.get('role') === 'DIMENSION'),
    isRelational: true,
    removeLastRowPercentiles: false,
    hasCurrencyDimension: currencyCodeColumn => reportDef.get('columns', ImmutableMap()).some((column, columnName) => columnName === currencyCodeColumn && column.get('role') === 'DIMENSION'),
    isMeasure: columnName => reportDef.getIn(['columns', columnName, 'role']) === 'MEASURE',
    getMeasureType: columnName => reportDef.getIn(['columns', columnName, 'role']) === 'MEASURE' ? reportDef.getIn(['columns', columnName, 'aggregation']) : SUM
  };
};
const addPropertyMeta = raasResponse => raasResponse.update('header', header => header.map(property => {
  const aggregationType = property.get('aggregationType');
  if (aggregationType) {
    return property.set('propertyMeta', ImmutableMap({
      aggregationType
    }));
  }
  return property;
}));
const processDataset = (reportDefinition, raasResponse, metaStateData, allJoinMetadata, gates) => {
  const datasetBuildOptions = Object.assign({}, getDatasetBuildOptionsFromReportDef(reportDefinition), {
    dataAge: raasResponse.get('dataAge')
  });
  const processedDataset = Dataset(buildDataset(addPropertyMeta(raasResponse), datasetBuildOptions));

  // Apply FE fixes to the RAAS datasets
  return applyRaaSDatasetFixes({
    reportDefinition,
    metaData: metaStateData,
    dataset: processedDataset,
    datasetBuildOptions,
    raasResponse,
    allJoinMetadata,
    gates
  });
};
const useGetProcessedDatasets = (reportDefinition, metaStateData, raasResponse, settled, allJoinMetadata, dataset, gates) => {
  const datasets = useMemo(() => {
    if (settled && raasResponse !== undefined && allJoinMetadata !== undefined && metaStateData !== undefined && dataset) {
      const datasetKey = 'primary';
      let processedDataSets = fromJS({
        [datasetKey]: dataset
      });
      const insightsDatasets = raasResponse.get('insights');
      const hasInsightsDatasets = insightsDatasets && insightsDatasets.count();
      if (hasInsightsDatasets) {
        insightsDatasets.forEach((insightsDataset, index) => {
          processedDataSets = processedDataSets.set(`${DATASET_INSIGHT_DATASET_PREFIX}-${datasetKey}-${index}`, processDataset(reportDefinition, insightsDataset.get('dataSet'), metaStateData, allJoinMetadata, gates).set('insightMeta', insightsDataset.get('insightMeta')).set('insightOption', insightsDataset.get('insightOption')));
        });
      }
      return processedDataSets;
    }
    return undefined;
  }, [metaStateData, reportDefinition, raasResponse, settled, allJoinMetadata, dataset, gates]);
  return datasets;
};
const useSnowflakeRaaS = (reportWithOptions, {
  reportId = undefined,
  onStart = emptyFunction,
  onSettled = emptyFunction,
  onError = emptyFunction,
  onDrilldown,
  onSort = undefined,
  onQuerySuccess = emptyFunction,
  isMobile,
  queryId,
  isValidation,
  compact = false,
  mobileReportingOptions = {}
} = {}, gates = []) => {
  const useQueryReportResolve = useMemo(() => {
    const isAsyncReportResolveUngated = isUngatedForGate({
      gateToBeChecked: relationalAsyncReportResolveGate,
      gates
    });
    return isAsyncReportResolveUngated ? useQueryRaasWithAsyncReportResolve : useQueryRaaS;
  }, [gates]);

  /**
   * meta: fetch meta data used across each stage of the pipeline
   * primary output: <ReportMeta>
   */
  const {
    reportDefinition
  } = reportWithOptions;
  const metaState = useMeta(reportDefinition);

  /**
   * query: queries for the raw dataset
   * primary output: <Dataset>
   */
  const {
    queryState,
    queryDefinition
  } = useQueryReportResolve(reportWithOptions, {
    reportId,
    queryId,
    isValidation,
    onSuccess: onQuerySuccess
  });
  const {
    loading: queryMetaLoading,
    settled,
    error: queryMetaError
  } = mergeAsyncState([metaState, queryState]);
  const {
    data: raasResponse
  } = queryState;
  const {
    data: allJoinMetadata,
    loading: joinMetaLoading,
    error: joinMetaError
  } = useSnowflakeJoinMetadata();
  const loading = queryMetaLoading || joinMetaLoading;
  const error = queryMetaError || joinMetaError;
  const dataset = useMemo(() => {
    if (settled && raasResponse !== undefined && allJoinMetadata !== undefined && metaState.data !== undefined) {
      return processDataset(reportDefinition, raasResponse, metaState.data, allJoinMetadata, gates);
    }
    return undefined;
  }, [metaState.data, reportDefinition, raasResponse, settled, allJoinMetadata, gates]);
  const datasets = useGetProcessedDatasets(reportDefinition, metaState.data, raasResponse, settled, allJoinMetadata, dataset, gates);

  /**
   * visualize: creates a visualization object
   * primary output: <Visualization>
   *
   * Important: similar to `process`, this stage is decoupled since there may be
   * changes that will only trigger the visualization. For example, we might
   * have the same-old-dataset, but visualize it differently.
   */
  const visualization = useVisualize(datasets, reportWithOptions, metaState.data, onDrilldown, onSort, isMobile, gates, compact, mobileReportingOptions);

  /**
   * client events / callbacks
   */

  const onStartRef = useUpdatingRef(onStart);
  useEffect(() => {
    if (loading) {
      onStartRef.current();
    }
  }, [loading, onStartRef]);
  const onSettledRef = useUpdatingRef(onSettled);
  useEffect(() => {
    if (settled) {
      onSettledRef.current(dataset);
    }
  }, [settled, onSettledRef, dataset]);
  const onErrorRef = useUpdatingRef(onError);
  useEffect(() => {
    if (error) {
      onErrorRef.current(error);
    }
  }, [settled, onErrorRef, error]);
  return {
    visualization,
    dataset,
    datasets,
    queryDefinition,
    loading,
    error
  };
};
export const __TESTABLE__ = {
  getDatasetBuildOptionsFromReportDef,
  useGetProcessedDatasets
};
export default useSnowflakeRaaS;